import errorHandler from './utils/errorHandler';
import React, { useState, useEffect } from 'react';
import { Paper, Typography, Snackbar, Alert, Button, Box } from '@mui/material';
import api from './axiosConfig';
import SearchEmployee from './SearchEmployee';
import ReviewForm from './ReviewForm';
import { useAuth } from './AuthContext';

const SubmitEmployeeReview = () => {
  const { user } = useAuth();
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [reviewData, setReviewData] = useState({
    employeeName: '',
    jobTitle: '',
    department: '',
    dateOfEngagement: '',
    manager: '',
    dateOfMeeting: '',
    objectives: '',
    achievements: '',
    strengths: '',
    needsImprovement: '',
    developmentActivities: '',
    careerPlanning: '',
    otherDiscussionPoints: '',
    clientService: [],
    retailSales: [],
    workManagement: [],
    teamwork: [],
    attendancePunctuality: [],
    communication: [],
    overallPerformanceRating: [],
    supervisorsComments: '',
    employeeComments: '',
    actionPlans: '',
    employeeSignature: '',
    appraiserSignature: '',
    reviewDate: '',
    reviewingManagerSignature: '',
    reviewingManagerDate: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    errorHandler.logDebug('SubmitEmployeeReview', 'SubmitEmployeeReview mounted');
    return () => errorHandler.logDebug('SubmitEmployeeReview', 'SubmitEmployeeReview unmounted');
  }, []);

  const handleEmployeeSelect = (employeeId) => {
    errorHandler.logDebug('SubmitEmployeeReview', 'Employee selected:', employeeId);
    setSelectedEmployeeId(employeeId);
    fetchReviewData(employeeId);
  };

  const fetchReviewData = async (employeeId) => {
    errorHandler.logApiCall('Fetching review data for employee:', employeeId);
    try {
      const response = await api.get(`/reviews/${employeeId}`);
      errorHandler.logDebug('SubmitEmployeeReview', 'Fetched review data:', response.data);
      if (response.data) {
        setReviewData(response.data);
      } else {
        errorHandler.logDebug('SubmitEmployeeReview', 'No existing review data found, initializing new review');
        setReviewData({
          ...reviewData,
          employeeId: employeeId,
        });
      }
      setError(null);
    } catch (error) {
      console.error('Error fetching review data:', error);
      if (error.response && error.response.status === 404) {
        errorHandler.logDebug('SubmitEmployeeReview', 'No existing review found, initializing new review');
        setReviewData({
          ...reviewData,
          employeeId: employeeId,
        });
        setError(null);
      } else {
        setError('Failed to fetch review data. Please try again.');
        setSnackbar({ open: true, message: 'Failed to fetch review data', severity: 'error' });
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setReviewData(prevData => ({ ...prevData, [name]: value }));
  };

  const handleCheckboxChange = (section, value) => {
    setReviewData((prevData) => {
      const updatedSection = prevData[section].includes(value)
        ? prevData[section].filter((item) => item !== value)
        : [...prevData[section], value];
      return { ...prevData, [section]: updatedSection };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await api.post(`/reviews/employee/${selectedEmployeeId}`, reviewData);
      errorHandler.logDebug('SubmitEmployeeReview', 'Review submitted successfully', response.data);
      setSnackbar({ open: true, message: 'Review submitted successfully', severity: 'success' });
    } catch (error) {
      console.error('Error submitting review', error);
      setError('Failed to submit review. Please try again.');
      setSnackbar({ open: true, message: 'Failed to submit review', severity: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveDraft = async () => {
    try {
      const draftReview = { ...reviewData, status: 'draft' };
      const response = await api.post(`/reviews/employee/${selectedEmployeeId}`, draftReview);
      errorHandler.logDebug('SubmitEmployeeReview', 'Draft saved successfully', response.data);
      setSnackbar({ open: true, message: 'Draft saved successfully', severity: 'success' });
    } catch (error) {
      console.error('Error saving draft', error);
      setSnackbar({ open: true, message: 'Failed to save draft', severity: 'error' });
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <Typography variant="h4" gutterBottom>Employee Review</Typography>
      {!selectedEmployeeId ? (
        <>
          <Typography variant="body1" gutterBottom>
            Please search and select an employee to review.
          </Typography>
          <SearchEmployee onEmployeeSelect={handleEmployeeSelect} />
        </>
      ) : (
        <>
          <ReviewForm 
            reviewData={reviewData}
            handleChange={handleChange}
            handleCheckboxChange={handleCheckboxChange}
            isSubmitting={isSubmitting}
            error={error}
          />
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Button 
              onClick={saveDraft} 
              disabled={isSubmitting}
              variant="outlined"
            >
              Save Draft
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={isSubmitting}
              variant="contained"
              color="primary"
            >
              Submit Review
            </Button>
          </Box>
        </>
      )}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default SubmitEmployeeReview;